import { z } from "zod";
import { createInterfaceId } from "@gitlab/needle";
import { NotificationType, RequestType } from "vscode-jsonrpc";
//#region src/types.ts
/**
* Base interface for all message configurations.
*
* Represents the common properties shared by all message types.
*/
/**
* Configuration for a notification message.
*
* A notification is a one-way message that does not expect a response.
*
* @template TParams - The type of parameters accepted by this message.
*/
/**
* Type guard that determines if an unknown item is a notification.
*
* @param {unknown} item - The item to test.
* @returns {item is RpcNotificationDefinition} - True if the item is a notification, otherwise false.
*/
const isNotification = (item) => {
	return typeof item === "object" && item !== null && "type" in item && item.type === "notification";
};
/**
* Configuration for a request-response message.
*
* A request-response message expects a response to be sent back.
*
* @template TParams - The type of parameters accepted by this message.
* @template TResponse - The type of response returned by this message.
*/
/**
* Type guard that determines if an unknown item is a request.
*
* @param {unknown} item - The item to test.
* @returns {item is RpcRequestDefinition} - True if the item is a request, otherwise false.
*/
const isRequest = (item) => {
	return typeof item === "object" && item !== null && "type" in item && item.type === "request";
};
/**
* Union type representing any type of message.
*
* A message can either be a notification or a request.
*/
//#endregion
//#region src/constants.ts
const NO_PARAMS = z.undefined();
const NO_RESPONSE = z.void();
//#endregion
//#region src/declaration/declare_notification.ts
var NotificationDeclarationBuilder = class {
	#definition;
	constructor(methodName, paramsSchema) {
		this.#definition = {
			type: "notification",
			methodName,
			paramsSchema
		};
	}
	withName(name) {
		this.#definition.name = name;
		return this;
	}
	withDescription(description) {
		this.#definition.description = description;
		return this;
	}
	withParams(paramsSchema) {
		const builder = this;
		builder.#definition.paramsSchema = paramsSchema;
		return builder;
	}
	build() {
		return this.#definition;
	}
};
const declareNotification = (method) => new NotificationDeclarationBuilder(method, NO_PARAMS);
//#endregion
//#region src/declaration/declare_request.ts
var RequestDeclarationBuilder = class {
	#definition;
	constructor(methodName, paramsSchema, responseSchema) {
		this.#definition = {
			type: "request",
			methodName,
			paramsSchema,
			responseSchema
		};
	}
	withName(name) {
		this.#definition.name = name;
		return this;
	}
	withDescription(description) {
		this.#definition.description = description;
		return this;
	}
	withParams(paramsSchema) {
		const builder = this;
		builder.#definition.paramsSchema = paramsSchema;
		return builder;
	}
	withResponse(responseSchema) {
		const builder = this;
		builder.#definition.responseSchema = responseSchema;
		return builder;
	}
	build() {
		return this.#definition;
	}
};
const declareRequest = (method) => new RequestDeclarationBuilder(method, NO_PARAMS, NO_RESPONSE);
//#endregion
//#region src/rpc_message_definition_source.ts
/**
* RpcMessageDefinitionSource represents a modular source of RPC message definitions.
* These sources can exist in various parts of the codebase, each contributing
* specific message definitions based on their domain or functionality.
*
* This modular approach allows for flexibility and scalability when adding new
* message definitions, as each source can be developed and maintained independently.
*/
/**
* Represents a source of server-to-client RPC message definitions.
* Multiple sources can be implemented to contribute various message definitions
* that are sent from the server to the client.
*/
const ServerToClientRpcMessageDefinitionSource = createInterfaceId("ServerToClientMessageDefinitionProvider");
/**
* Represents a source of client-to-server RPC message definitions.
* Multiple sources can be implemented to contribute various message definitions
* that are sent from the client to the server.
*/
const ClientToServerRpcMessageDefinitionSource = createInterfaceId("ClientToServerMessageDefinitionProvider");
//#endregion
//#region src/rpc_message_definition_provider.ts
/**
* RpcMessageDefinitionProvider acts as an aggregator that collects message
* definitions from various RpcMessageDefinitionSources across the codebase.
* While multiple sources contribute specific definitions, the provider consolidates
* them to present a comprehensive set of available messages.
*
* This structure ensures a centralized view of all message definitions, which
* is useful for validation, schema generation, and documentation purposes.
*/
/**
* Aggregates all server-to-client message definitions from various sources.
* This provider serves as a central point to retrieve the complete set of
* server-to-client messages used in the system.
*/
const ServerToClientRpcMessageDefinitionProvider = createInterfaceId("ServerToClientMessageDefinitionProvider");
/**
* Aggregates all client-to-server message definitions from various sources.
* This provider serves as a central point to retrieve the complete set of
* client-to-server messages used in the system.
*/
const ClientToServerRpcMessageDefinitionProvider = createInterfaceId("ClientToServerMessageDefinitionProvider");
//#endregion
//#region src/utils/vscode_rpc_utils.ts
/**
* Converts an RpcNotificationDefinition into a vscode-jsonrpc NotificationType.
*
* @param notification - The RpcNotificationDefinition to convert.
* @returns A NotificationType compatible with vscode-jsonrpc.
*/
const toVSCodeNotificationType = (notification) => {
	return new NotificationType(notification.methodName);
};
/**
* Converts an RpcRequestDefinition into a vscode-jsonrpc RequestType.
*
* @param request - The RpcRequestDefinition to convert.
* @returns A RequestType compatible with vscode-jsonrpc.
*/
const toVSCodeRequestType = (request) => {
	return new RequestType(request.methodName);
};
//#endregion
export { ClientToServerRpcMessageDefinitionProvider, ClientToServerRpcMessageDefinitionSource, NO_PARAMS, NO_RESPONSE, ServerToClientRpcMessageDefinitionProvider, ServerToClientRpcMessageDefinitionSource, declareNotification, declareRequest, isNotification, isRequest, toVSCodeNotificationType, toVSCodeRequestType };

//# sourceMappingURL=index.mjs.map